import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import type { ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import { politicasSimulacaoService } from '@/services/politicasSimulacaoService'
import { produtoService } from '@/services/produtoService'
import { parceirosService } from '@/services/parceirosService'
import type {
  FaixaComissao,
  PoliticaComissaoRequest,
  PoliticaComissaoResponse,
  PoliticaProdutoRequest,
  PoliticaProdutoResponse,
  PoliticaSpreadRequest,
  PoliticaSpreadResponse,
  PoliticaTributariaRequest,
  PoliticaTributariaResponse,
  TipoPoliticaSimulacao,
} from '@/types/simulacaoComissao'
import { ROTULO_BASE_TRIBUTO, ROTULO_MODELO, ROTULO_NATUREZA_TRIBUTO } from '@/types/simulacaoComissao'
import { mensagemErroPolitica } from '@/services/politicaProdutoService'
import type { Parceiro, Produto } from '@/types/produto'
import { categoriaRetirada } from '@/constants/categoriasProduto'
import { CODIGO_OUTRA_NECESSIDADE } from '@/constants/codigosProduto'
import { brl, pctDeFracao } from './fracao'
import { SecaoPolitica, type PoliticaVersionada } from './politicas/SecaoPolitica'
import NovaPoliticaProdutoDialog from './politicas/NovaPoliticaProdutoDialog'
import NovoSpreadDialog from './politicas/NovoSpreadDialog'
import NovoTributoDialog from './politicas/NovoTributoDialog'
import NovaPoliticaComissaoDialog from './politicas/NovaPoliticaComissaoDialog'

const temPapel = (permissoes: string[] | undefined, ...papeis: string[]) =>
  !!permissoes?.some((p) => papeis.includes(p.toUpperCase()))

const ehStatus403 = (e: unknown) => (e as { status?: number })?.status === 403
const mensagemDe = (e: unknown, padrao: string) => (e as { message?: string })?.message ?? padrao

type Encerrar =
  | { tipo: 'comissao'; item: PoliticaComissaoResponse }
  | { tipo: 'produto'; item: PoliticaProdutoResponse }
  | { tipo: 'spread'; item: PoliticaSpreadResponse }
  | { tipo: 'tributo'; item: PoliticaTributariaResponse }

type TipoTela = Encerrar['tipo']

/** P6 (AN-PP6 §3.4): segmento da rota de aprovar/rejeitar por seção. */
const SEGMENTO: Record<TipoTela, TipoPoliticaSimulacao> = {
  comissao: 'comissao',
  produto: 'produtos',
  spread: 'spreads',
  tributo: 'tributos',
}

/** Papéis que LEEM a política de comissão vigente (`GET /comissao/vigente`, CONSULTOR_OR_ABOVE) ou a lista (FINANCEIRO). */
const PAPEIS_LEITURA_COMISSAO = ['CONSULTOR', 'OPERADOR', 'GERENTE', 'ADMIN', 'FINANCEIRO']

/** Aviso depois de propor (202 PENDENTE_APROVACAO): nada muda no cálculo até a aprovação. */
export const avisoProposta = (versao?: string | null): string =>
  `Proposta${versao ? ` ${versao}` : ''} enviada para aprovação: só vale no cálculo depois de aprovada por outra pessoa.`

/** Aviso do "Encerrar" da comissão (AN-PP4 §2.5/§12 F1): a comissão é global. */
export const AVISO_ENCERRAR_COMISSAO =
  'Todas as simulações calculáveis passam a bloquear até uma nova versão ser publicada.'

/** Uma linha por faixa, só com o que o backend devolveu (rótulo, limite, inclusividade, %). */
const textoFaixa = (f: FaixaComissao): string => {
  const limite =
    f.valorAte == null ? 'sem teto' : `até ${brl(f.valorAte)} ${f.ateInclusivo ? '(inclusive)' : '(exclusive)'}`
  return `${f.rotulo}: ${limite} → ${pctDeFracao(f.percentual)}`
}

/**
 * Políticas de simulação (ADMIN) — `/simulacoes/politicas`, plano §3.4 / bloco 8 D2–D5.
 *
 * P4 (AN-PP4 §2.5): primeira seção "Política de comissão" — faixas por valor da operação e o
 * percentual do Sacado/Sacador 360, GLOBAIS e versionados. Tudo vem do backend (nenhum número
 * fixo na tela). ADMIN vê o histórico e publica/encerra; CONSULTOR/OPERADOR/GERENTE só leem a
 * versão vigente (somente leitura; as demais seções continuam só do ADMIN).
 *
 * Três seções, todas dado versionado com vigência semiaberta e origem: modelo por produto,
 * spread por parceiro × produto e alíquotas de tributos. Cadastrar é criar uma NOVA versão
 * (o backend encerra a anterior na data de início); "Encerrar vigência" fecha hoje. Nenhum
 * spread ou alíquota tem valor padrão nesta tela — o parâmetro é decisão do Financeiro,
 * registrada pela origem (e-mail, ata, contrato). Percentual na UI, fração na API.
 *
 * Escopo: dado global da Originei, protegido pelo papel — ADMIN ou FINANCEIRO no backend (403 para os
 * demais) e aqui; sem permissão a tela nem chama a API.
 *
 * P6 (AN-PP6 §3/§8): cadastrar é PROPOR — o POST responde 202 com a versão PENDENTE_APROVACAO, que não
 * vale no cálculo; outra pessoa aprova ou rejeita (FINANCEIRO; spread/tributo também ADMIN). Quem propôs
 * vê os botões desabilitados, com o motivo; o papel exato é do backend (403 `APROVADOR_SEM_PAPEL` como
 * veio). Propor produto/comissão e encerrar são do ADMIN; spread/tributo, FINANCEIRO ou ADMIN.
 */
export const PoliticasSimulacaoPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const ehAdmin = useMemo(() => temPapel(usuario?.permissoes, 'ADMIN'), [usuario])
  // P5 (AN-PP5 §1.3): a persona consultor nunca vê o histórico, nem com FINANCEIRO residual.
  const podeVer = useMemo(
    () => !ehConsultor(usuario?.permissoes) && temPapel(usuario?.permissoes, 'ADMIN', 'FINANCEIRO'),
    [usuario]
  )
  const usuarioId = usuario?.id ?? null
  const podeLerComissao = useMemo(
    () => temPapel(usuario?.permissoes, ...PAPEIS_LEITURA_COMISSAO),
    [usuario]
  )

  const [comissao, setComissao] = useState<PoliticaComissaoResponse[] | null>(null)
  const [erroComissao, setErroComissao] = useState<string | null>(null)

  const [politicasProduto, setPoliticasProduto] = useState<PoliticaProdutoResponse[] | null>(null)
  const [spreads, setSpreads] = useState<PoliticaSpreadResponse[] | null>(null)
  const [tributos, setTributos] = useState<PoliticaTributariaResponse[] | null>(null)
  const [erroProdutos, setErroProdutos] = useState<string | null>(null)
  const [erroSpreads, setErroSpreads] = useState<string | null>(null)
  const [erroTributos, setErroTributos] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)

  const [catalogo, setCatalogo] = useState<Produto[]>([])
  const [parceiros, setParceiros] = useState<Parceiro[]>([])
  const [erroListas, setErroListas] = useState<string | null>(null)

  const [dialogo, setDialogo] = useState<'comissao' | 'produto' | 'spread' | 'tributo' | null>(null)
  const [encerrar, setEncerrar] = useState<Encerrar | null>(null)
  const [encerrando, setEncerrando] = useState(false)
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [decidindo, setDecidindo] = useState<string | null>(null)
  const [rejeitando, setRejeitando] = useState<Encerrar | null>(null)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  const [erroRejeicao, setErroRejeicao] = useState<string | null>(null)

  // ADMIN: histórico completo (`GET /comissao`); demais: só a vigente (`GET /comissao/vigente`).
  const carregarComissao = useCallback(async () => {
    setErroComissao(null)
    try {
      if (podeVer) {
        setComissao(await politicasSimulacaoService.listarComissao())
      } else {
        const vigente = await politicasSimulacaoService.comissaoVigente()
        // A rota só devolve a versão vigente: é ela, por definição, a "vigente".
        setComissao(vigente ? [{ ...vigente, vigente: true }] : [])
      }
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setComissao(null)
      setErroComissao(mensagemDe(e, 'Não foi possível carregar a política de comissão'))
    }
  }, [podeVer])

  const carregarProdutos = useCallback(async () => {
    setErroProdutos(null)
    try {
      setPoliticasProduto(await politicasSimulacaoService.listarProdutos())
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setPoliticasProduto(null)
      setErroProdutos(mensagemDe(e, 'Não foi possível carregar as políticas de produto'))
    }
  }, [])

  const carregarSpreads = useCallback(async () => {
    setErroSpreads(null)
    try {
      setSpreads(await politicasSimulacaoService.listarSpreads())
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setSpreads(null)
      setErroSpreads(mensagemDe(e, 'Não foi possível carregar os spreads'))
    }
  }, [])

  const carregarTributos = useCallback(async () => {
    setErroTributos(null)
    try {
      setTributos(await politicasSimulacaoService.listarTributos())
    } catch (e) {
      if (ehStatus403(e)) setSemPermissao(true)
      setTributos(null)
      setErroTributos(mensagemDe(e, 'Não foi possível carregar os tributos'))
    }
  }, [])

  const carregarListas = useCallback(async () => {
    setErroListas(null)
    const [cat, parc] = await Promise.allSettled([produtoService.listar(0, 200), parceirosService.listar(true)])
    if (cat.status === 'fulfilled') setCatalogo(cat.value?.content ?? [])
    if (parc.status === 'fulfilled') setParceiros(parc.value ?? [])
    if (cat.status === 'rejected' || parc.status === 'rejected') {
      setErroListas('Catálogo de produtos ou parceiros indisponível — os formulários podem ficar sem opções.')
    }
  }, [])

  useEffect(() => {
    if (!podeLerComissao) return
    void carregarComissao()
    if (!podeVer) return
    void carregarProdutos()
    void carregarSpreads()
    void carregarTributos()
    void carregarListas()
  }, [podeVer, podeLerComissao, carregarComissao, carregarProdutos, carregarSpreads, carregarTributos, carregarListas])

  const nomeDoProduto = useCallback(
    (id: string) => catalogo.find((p) => p.id === id)?.nome ?? id,
    [catalogo]
  )
  // Produto de categoria retirada (Duplicata Escritural, P0) não aceita política nova nem conta como
  // "sem política vigente"; o catálogo completo continua só para dar nome ao histórico.
  // P1 (AN-PP1 §2.4/§3.2): idem para status RETIRADA, e "Outra necessidade" (OTHER_NEED) nunca tem
  // política — fica fora dos selects e da lista de "sem política".
  const catalogoVigente = useMemo(
    () =>
      catalogo.filter(
        (p) =>
          !categoriaRetirada(p.categoria) &&
          p.status !== 'RETIRADA' &&
          p.codigo !== CODIGO_OUTRA_NECESSIDADE &&
          p.categoria !== 'OUTRA_NECESSIDADE'
      ),
    [catalogo]
  )

  const recarregar = (tipo: TipoTela) =>
    tipo === 'comissao'
      ? carregarComissao()
      : tipo === 'produto'
        ? carregarProdutos()
        : tipo === 'spread'
          ? carregarSpreads()
          : carregarTributos()

  // ── propor (P6: 202 PENDENTE_APROVACAO; nada muda no cálculo até a aprovação) ──
  const criarComissao = async (r: PoliticaComissaoRequest) => {
    const proposta = await politicasSimulacaoService.criarComissao(r)
    setAviso(avisoProposta(proposta?.versao))
    await carregarComissao()
  }
  const criarProduto = async (r: PoliticaProdutoRequest) => {
    const proposta = await politicasSimulacaoService.criarProduto(r)
    setAviso(avisoProposta(proposta?.versao))
    await carregarProdutos()
  }
  const criarSpread = async (r: PoliticaSpreadRequest) => {
    const proposta = await politicasSimulacaoService.criarSpread(r)
    setAviso(avisoProposta(proposta?.versao))
    await carregarSpreads()
  }
  const criarTributo = async (r: PoliticaTributariaRequest) => {
    const proposta = await politicasSimulacaoService.criarTributo(r)
    setAviso(avisoProposta(proposta?.versao))
    await carregarTributos()
  }

  // ── aprovar / rejeitar (outra pessoa; erros 403/409/422 como vierem) ──
  const aprovar = async (x: Encerrar) => {
    setDecidindo(x.item.id)
    setErroAcao(null)
    setAviso(null)
    try {
      await politicasSimulacaoService.aprovar(SEGMENTO[x.tipo], x.item.id, x.item.versaoRegistro)
      setAviso(`Versão ${x.item.versao} aprovada: passa a valer no cálculo a partir do início da vigência.`)
      await recarregar(x.tipo)
    } catch (e) {
      setErroAcao(mensagemErroPolitica(e, 'Não foi possível aprovar a proposta'))
    } finally {
      setDecidindo(null)
    }
  }

  const confirmarRejeicao = async () => {
    const x = rejeitando
    if (!x || !motivoRejeicao.trim()) return
    setDecidindo(x.item.id)
    setErroRejeicao(null)
    setAviso(null)
    try {
      await politicasSimulacaoService.rejeitar(SEGMENTO[x.tipo], x.item.id, motivoRejeicao.trim(), x.item.versaoRegistro)
      setAviso(`Versão ${x.item.versao} rejeitada; fica no histórico.`)
      setRejeitando(null)
      setMotivoRejeicao('')
      await recarregar(x.tipo)
    } catch (e) {
      setErroRejeicao(mensagemErroPolitica(e, 'Não foi possível rejeitar a proposta'))
    } finally {
      setDecidindo(null)
    }
  }

  /** Revisão PP6-valr (8b): o proponente retira a própria proposta — libera a chave para uma nova. */
  const retirar = async (x: Encerrar) => {
    setDecidindo(x.item.id)
    setErroAcao(null)
    setAviso(null)
    try {
      await politicasSimulacaoService.retirar(SEGMENTO[x.tipo], x.item.id)
      setAviso(`Proposta ${x.item.versao} retirada; fica no histórico como rejeitada.`)
      await recarregar(x.tipo)
    } catch (e) {
      setErroAcao(mensagemErroPolitica(e, 'Não foi possível retirar a proposta'))
    } finally {
      setDecidindo(null)
    }
  }

  const abrirRejeicao = (x: Encerrar) => {
    setMotivoRejeicao('')
    setErroRejeicao(null)
    setRejeitando(x)
  }

  /** Props comuns de decisão de cada seção. */
  const decisao = {
    podeDecidir: podeVer,
    usuarioId,
    decidindo,
  }

  // ── encerrar vigência (com confirmação) ──
  const confirmarEncerrar = async () => {
    if (!encerrar) return
    setEncerrando(true)
    setErroAcao(null)
    try {
      if (encerrar.tipo === 'comissao') {
        await politicasSimulacaoService.encerrarComissao(encerrar.item.id)
        await carregarComissao()
      } else if (encerrar.tipo === 'produto') {
        await politicasSimulacaoService.encerrarProduto(encerrar.item.id)
        await carregarProdutos()
      } else if (encerrar.tipo === 'spread') {
        await politicasSimulacaoService.encerrarSpread(encerrar.item.id)
        await carregarSpreads()
      } else {
        await politicasSimulacaoService.encerrarTributo(encerrar.item.id)
        await carregarTributos()
      }
      setEncerrar(null)
    } catch (e) {
      setErroAcao(mensagemDe(e, 'Não foi possível encerrar a vigência'))
    } finally {
      setEncerrando(false)
    }
  }

  const descricaoEncerrar = (x: Encerrar): string => {
    switch (x.tipo) {
      case 'comissao':
        return `política de comissão (${x.item.versao})`
      case 'produto':
        return `política de ${x.item.produtoNome} (${x.item.versao})`
      case 'spread':
        return `spread de ${x.item.parceiroNome ?? x.item.parceiroId} × ${nomeDoProduto(x.item.produtoId)} (${x.item.versao})`
      default:
        return `alíquota ${x.item.nome}${x.item.descricao ? ` — ${x.item.descricao}` : ''} (${x.item.versao})`
    }
  }

  const colunasComissao: ColunaTabelaAcao<PoliticaComissaoResponse>[] = [
    {
      titulo: 'Faixas por valor da operação',
      render: (c) => (
        <Stack spacing={0.25} data-testid={`faixas-${c.id}`}>
          {[...c.faixas]
            .sort((a, b) => a.ordem - b.ordem)
            .map((f) => (
              <Typography key={f.ordem} variant="body2">
                {textoFaixa(f)}
              </Typography>
            ))}
          <Typography variant="caption" color="textSecondary">
            Tabela {c.versaoFaixas}
          </Typography>
        </Stack>
      ),
    },
    {
      titulo: 'Sacado/Sacador 360',
      render: (c) => `${pctDeFracao(c.percentualReceitaLiquida360)} da receita líquida Originei`,
    },
  ]
  const comissaoVigente = useMemo(() => (comissao ?? []).find((c) => c.vigente) ?? null, [comissao])

  const colunasProduto: ColunaTabelaAcao<PoliticaProdutoResponse>[] = [
    { titulo: 'Produto', render: (p) => <Typography variant="body2" fontWeight={600}>{p.produtoNome}</Typography> },
    { titulo: 'Modelo', render: (p) => ROTULO_MODELO[p.modelo] ?? p.modelo },
    { titulo: 'Natureza', render: (p) => p.natureza ?? '—' },
  ]
  const colunasSpread: ColunaTabelaAcao<PoliticaSpreadResponse>[] = [
    { titulo: 'Parceiro', render: (p) => <Typography variant="body2" fontWeight={600}>{p.parceiroNome ?? p.parceiroId}</Typography> },
    { titulo: 'Produto', render: (p) => nomeDoProduto(p.produtoId) },
    { titulo: 'Spread', render: (p) => pctDeFracao(p.spread), align: 'right' },
  ]
  const colunasTributo: ColunaTabelaAcao<PoliticaTributariaResponse>[] = [
    {
      titulo: 'Tributo',
      render: (t) => (
        <Typography variant="body2" fontWeight={600}>
          {t.nome}
          {t.descricao ? ` — ${t.descricao}` : ''}
        </Typography>
      ),
    },
    { titulo: 'Alíquota', render: (t) => pctDeFracao(t.aliquota), align: 'right' },
    { titulo: 'Base', render: (t) => ROTULO_BASE_TRIBUTO[t.base] ?? t.base },
    {
      titulo: 'Natureza',
      render: (t) =>
        t.natureza ? ROTULO_NATUREZA_TRIBUTO[t.natureza as keyof typeof ROTULO_NATUREZA_TRIBUTO] ?? t.natureza : '—',
    },
  ]

  const cabecalho = (
    <PageHeader
      title="Políticas de simulação"
      eyebrow="Administração"
      subtitle="Comissão, modelo por produto, spreads por parceiro e alíquotas — versionados, com vigência e origem"
    />
  )

  const secaoComissao = (
    <SecaoPolitica<PoliticaComissaoResponse>
      titulo="Política de comissão"
      descricao="Faixas gerais por valor da operação (produtos de faixa e Campanha de Incentivo) e o percentual da receita líquida do Sacado/Sacador 360 — uma política global."
      rotuloNovo="Propor versão de comissão"
      itens={comissao}
      erro={erroComissao}
      colunas={colunasComissao}
      mensagemVazio="Nenhuma política de comissão vigente — toda simulação calculável está bloqueada"
      onNovaVersao={() => setDialogo('comissao')}
      onEncerrar={(item) => setEncerrar({ tipo: 'comissao', item })}
      onTentarNovamente={() => void carregarComissao()}
      somenteLeitura={!podeVer}
      podePropor={ehAdmin}
      podeEncerrar={ehAdmin}
      {...decisao}
      onAprovar={(item) => void aprovar({ tipo: 'comissao', item })}
      onRejeitar={(item) => abrirRejeicao({ tipo: 'comissao', item })}
      onRetirar={(item) => void retirar({ tipo: 'comissao', item })}
      aria-label="Política de comissão vigente"
    />
  )

  if ((usuario && !podeLerComissao) || semPermissao) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="As políticas de simulação são propostas pelo administrador da Originei (spread e tributos também pelo Financeiro) e aprovadas por outra pessoa."
        />
      </Box>
    )
  }

  if (!podeVer) {
    return (
      <Box>
        {cabecalho}
        <Alert severity="info" sx={{ mb: 2 }}>
          Somente leitura: a política de comissão é publicada pelo administrador da Originei, com a
          origem aprovada pelo Financeiro.
        </Alert>
        {secaoComissao}
      </Box>
    )
  }

  return (
    <Box>
      {cabecalho}

      <Alert severity="info" sx={{ mb: 2 }}>
        Sem política vigente a simulação do produto fica <strong>bloqueada</strong> para o
        consultor (revisão administrativa). Nenhum spread ou alíquota é assumido pelo sistema:
        cada versão registra a origem. Toda versão nova nasce <strong>pendente</strong> e só vale
        depois de aprovada por outra pessoa (modelo por produto e comissão: Financeiro).
      </Alert>
      {aviso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso(null)} data-testid="aviso-politica">
          {aviso}
        </Alert>
      )}
      {erroListas && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {erroListas}
        </Alert>
      )}
      {erroAcao && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroAcao(null)} data-testid="erro-acao-politica">
          {erroAcao}
        </Alert>
      )}

      <Stack spacing={2}>
        {secaoComissao}

        <SecaoPolitica<PoliticaProdutoResponse>
          titulo="Modelo por produto"
          descricao="Qual fórmula do motor vale para cada produto do catálogo e a sua natureza (recorrente/pontual)."
          rotuloNovo="Propor versão de produto"
          itens={politicasProduto}
          erro={erroProdutos}
          colunas={colunasProduto}
          mensagemVazio="Nenhuma política de produto vigente — toda simulação por produto está bloqueada"
          onNovaVersao={() => setDialogo('produto')}
          onEncerrar={(item) => setEncerrar({ tipo: 'produto', item })}
          onTentarNovamente={() => void carregarProdutos()}
          podePropor={ehAdmin}
          podeEncerrar={ehAdmin}
          {...decisao}
          onAprovar={(item) => void aprovar({ tipo: 'produto', item })}
          onRejeitar={(item) => abrirRejeicao({ tipo: 'produto', item })}
          onRetirar={(item) => void retirar({ tipo: 'produto', item })}
          aria-label="Políticas de produto vigentes"
        >
          <ProdutosSemPolitica catalogo={catalogoVigente} politicas={politicasProduto} />
        </SecaoPolitica>

        <SecaoPolitica<PoliticaSpreadResponse>
          titulo="Spreads por parceiro × produto"
          descricao="Receita da Originei por operação (valor × spread). Só os modelos Recorrente e Pontual usam spread."
          rotuloNovo="Propor spread"
          itens={spreads}
          erro={erroSpreads}
          colunas={colunasSpread}
          mensagemVazio="Nenhum spread vigente — simulações Recorrente/Pontual bloqueadas"
          onNovaVersao={() => setDialogo('spread')}
          onEncerrar={(item) => setEncerrar({ tipo: 'spread', item })}
          onTentarNovamente={() => void carregarSpreads()}
          podePropor={podeVer}
          podeEncerrar={ehAdmin}
          {...decisao}
          onAprovar={(item) => void aprovar({ tipo: 'spread', item })}
          onRejeitar={(item) => abrirRejeicao({ tipo: 'spread', item })}
          onRetirar={(item) => void retirar({ tipo: 'spread', item })}
          aria-label="Spreads vigentes"
        />

        <SecaoPolitica<PoliticaTributariaResponse>
          titulo="Tributos"
          descricao="Alíquotas (CSLL, IR, outros) sobre a comissão bruta; o líquido é max(0, bruta − tributos)."
          rotuloNovo="Propor alíquota"
          itens={tributos}
          erro={erroTributos}
          colunas={colunasTributo}
          mensagemVazio="Nenhuma alíquota vigente — toda simulação com líquido está bloqueada"
          onNovaVersao={() => setDialogo('tributo')}
          onEncerrar={(item) => setEncerrar({ tipo: 'tributo', item })}
          onTentarNovamente={() => void carregarTributos()}
          podePropor={podeVer}
          podeEncerrar={ehAdmin}
          {...decisao}
          onAprovar={(item) => void aprovar({ tipo: 'tributo', item })}
          onRejeitar={(item) => abrirRejeicao({ tipo: 'tributo', item })}
          onRetirar={(item) => void retirar({ tipo: 'tributo', item })}
          aria-label="Alíquotas vigentes"
        />
      </Stack>

      <NovaPoliticaComissaoDialog
        aberto={dialogo === 'comissao'}
        base={comissaoVigente}
        onFechar={() => setDialogo(null)}
        onSalvar={criarComissao}
      />
      <NovaPoliticaProdutoDialog
        aberto={dialogo === 'produto'}
        produtos={catalogoVigente}
        onFechar={() => setDialogo(null)}
        onSalvar={criarProduto}
      />
      <NovoSpreadDialog
        aberto={dialogo === 'spread'}
        parceiros={parceiros}
        produtos={catalogoVigente}
        onFechar={() => setDialogo(null)}
        onSalvar={criarSpread}
      />
      <NovoTributoDialog aberto={dialogo === 'tributo'} onFechar={() => setDialogo(null)} onSalvar={criarTributo} />

      <ConfirmDialog
        open={!!encerrar}
        title="Encerrar vigência"
        message={
          encerrar
            ? encerrar.tipo === 'comissao'
              ? `Encerrar hoje a ${descricaoEncerrar(encerrar)}? ${AVISO_ENCERRAR_COMISSAO} O histórico é mantido.`
              : `Encerrar hoje a ${descricaoEncerrar(encerrar)}? A partir de agora as simulações que dependem dela ficam bloqueadas até uma nova versão. O histórico é mantido.`
            : ''
        }
        confirmLabel="Encerrar vigência"
        isLoading={encerrando}
        isDangerous
        onConfirm={() => void confirmarEncerrar()}
        onCancel={() => setEncerrar(null)}
      />

      <Dialog open={!!rejeitando} onClose={() => (decidindo ? undefined : setRejeitando(null))} fullWidth maxWidth="sm">
        <DialogTitle>Rejeitar proposta {rejeitando ? rejeitando.item.versao : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A versão fica no histórico como rejeitada e não vale no cálculo. O motivo é obrigatório e fica registrado.
          </Typography>
          {erroRejeicao && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="erro-rejeicao-politica">
              {erroRejeicao}
            </Alert>
          )}
          <TextField
            label="Motivo"
            value={motivoRejeicao}
            onChange={(e) => setMotivoRejeicao(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitando(null)} disabled={!!decidindo}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!motivoRejeicao.trim() || !!decidindo}
            onClick={() => void confirmarRejeicao()}
          >
            Confirmar rejeição
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/** Produtos do catálogo ainda sem política vigente — o que o consultor vê como bloqueado. */
const ProdutosSemPolitica: FC<{ catalogo: Produto[]; politicas: PoliticaVersionada[] | null }> = ({
  catalogo,
  politicas,
}) => {
  const vigentes = new Set((politicas ?? []).filter((p) => p.vigente).map((p) => (p as PoliticaProdutoResponse).produtoId))
  const sem = catalogo.filter((p) => !vigentes.has(p.id))
  if (!politicas || sem.length === 0) return null
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} data-testid="produtos-sem-politica">
      <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
        Sem política vigente:
      </Typography>
      {sem.map((p) => (
        <Chip key={p.id} size="small" color="warning" variant="outlined" label={p.nome} />
      ))}
    </Stack>
  )
}

export default PoliticasSimulacaoPage
